'use client';

import JobsIconSvg from '@/assets/svgs/main/jobs-filled.svg';
import {
  useAssignCanvasserToJob,
  useUnassignCanvasserFromJob,
} from '@/containers/Jobs/api';
import { useOptimisticJobs } from '@/containers/Jobs/hooks/useOptimisticJobs';
import { Job } from '@/containers/Jobs/types';
import { Modals } from '@/containers/Modals/all-modals';
import { useToast } from '@/containers/ToastMessage';
import { selectCurrentAccountId } from '@/lib/redux/app.selectors';
import { AssignmentList } from '@/shared/components/AssignmentList';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { AssignJobsModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import JobItemView from './JobItemView';

interface AssignJobsModalProps extends ModalProps {
  params?: AssignJobsModalParams;
}

const modalName = Modals.AssignJobsModal;

const AssignJobsModal = ({
  className,
  params,
  variant = 'narrow',
}: AssignJobsModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const { onClose, isModalOpen } = useModals({
    modalName,
  });

  // Modal Specific details
  const canvasserId = params?.canvasserId || '';
  const initialAssignedJobIds = useMemo(
    () => params?.assignedJobIds || [],
    [params?.assignedJobIds]
  );
  const [assignedJobIds, setAssignedJobIds] = useState<string[]>(initialAssignedJobIds);

  // Re-sync local state when the modal is reopened with new params
  useEffect(() => {
    if (isModalOpen) {
      setAssignedJobIds(initialAssignedJobIds);
    }
  }, [isModalOpen, initialAssignedJobIds]);

  const { showSuccess, showError } = useToast();

  // Get current account ID
  const currentAccountId = useSelector(selectCurrentAccountId);

  const [searchText, setSearchText] = useState('');
  const [shouldRefreshList, setShouldRefreshList] = useState(false);

  // Use optimistic jobs hook for search and filtering support
  const {
    data: jobs,
    isLoading: isLoadingJobs,
    refetch: refetchJobs,
  } = useOptimisticJobs(
    {
      accountId: currentAccountId || '',
      filter: 'current', // Only show jobs for current account
      search: searchText,
      sort: { column: null, direction: null }, // No sorting needed
    },
    { enableRefresh: false } // Disable auto-refresh in modal
  );

  // Mutations for job assignment/unassignment
  const assignCanvasserMutation = useAssignCanvasserToJob();
  const unassignCanvasserMutation = useUnassignCanvasserFromJob();

  // Refresh list
  useEffect(() => {
    if (shouldRefreshList) {
      refetchJobs();
      setShouldRefreshList(false);
    }
  }, [refetchJobs, shouldRefreshList]);

  // Track search empty state for modal styling
  useEffect(() => {
    setIsSearchEmpty(searchText === '');
  }, [searchText]);

  // Filter jobs into assigned and available (search is handled by useOptimisticJobs)
  const assignedJobs = useMemo(() => {
    return (jobs || []).filter((job) => {
      return assignedJobIds.includes(job.p2p_id);
    });
  }, [jobs, assignedJobIds]);

  const availableJobs = useMemo(() => {
    return (jobs || []).filter((job) => {
      return !assignedJobIds.includes(job.p2p_id);
    });
  }, [jobs, assignedJobIds]);

  // Handle individual job assignment
  const handleAssignJob = async (job: Job) => {
    const jobId = job.p2p_id;
    const jobName = job.name || job.p2p_name || 'Untitled Job';

    try {
      await assignCanvasserMutation.mutateAsync({
        jobId,
        canvasserId,
        isResponder: true,
      });
      showSuccess(`Successfully assigned to job "${jobName}"`);
      setAssignedJobIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
      setShouldRefreshList(true);
      // Update the assigned job IDs in the modal params would require parent state management
      // For now, we'll refresh the canvasser details when this modal closes
    } catch (error) {
      showError(`Failed to assign to job "${jobName}"`);
    }
  };

  // Handle individual job unassignment
  const handleUnassignJob = async (job: Job) => {
    const jobId = job.p2p_id;
    const jobName = job.name || job.p2p_name || 'Untitled Job';

    try {
      await unassignCanvasserMutation.mutateAsync({
        jobId,
        canvasserId,
      });
      showSuccess(`Successfully unassigned from job "${jobName}"`);
      setAssignedJobIds((prev) => prev.filter((id) => id !== jobId));
      setShouldRefreshList(true);
      // Update the assigned job IDs in the modal params would require parent state management
      // For now, we'll refresh the canvasser details when this modal closes
    } catch (error) {
      showError(`Failed to unassign from job "${jobName}"`);
    }
  };

  // Handle bulk assignment
  const handleAssignAll = async () => {
    const jobsToAssign = availableJobs;
    const results = await Promise.allSettled(
      jobsToAssign.map((job) => {
        const jobId = job.p2p_id;
        return assignCanvasserMutation.mutateAsync({
          jobId,
          canvasserId,
          isResponder: true,
        });
      })
    );

    const successful = results.filter((result) => result.status === 'fulfilled').length;
    const failed = results.filter((result) => result.status === 'rejected').length;

    if (successful > 0) {
      showSuccess(
        `Successfully assigned to ${successful} job${successful > 1 ? 's' : ''}`
      );
    }
    if (failed > 0) {
      showError(`Failed to assign to ${failed} job${failed > 1 ? 's' : ''}`);
    }

    // Mark all jobs that succeeded as assigned in local state
    const successfulJobIds = jobsToAssign
      .filter((_, idx) => results[idx]?.status === 'fulfilled')
      .map((job) => job.p2p_id);
    if (successfulJobIds.length) {
      setAssignedJobIds((prev) => Array.from(new Set([...prev, ...successfulJobIds])));
    }

    setShouldRefreshList(true);
  };

  const unmount = () => {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={isSearchEmpty}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <div className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<JobsIconSvg className="h-5 w-5" />}
            title="Jobs"
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="px-5 !py-0">
            {/* Spacing for consistency */}
            <div className="h-6" />

            {/* Search */}
            <div className="search-container mb-6">
              <SearchBar
                search={searchText}
                setSearch={setSearchText}
                placeholder="Search jobs..."
                debounceMs={500}
                ariaLabel="Search jobs"
                className="w-full"
              />
            </div>

            {/* Assignment List */}
            <AssignmentList
              assignedItems={assignedJobs}
              availableItems={availableJobs}
              LineItemView={JobItemView}
              onAssign={handleAssignJob}
              onUnassign={handleUnassignJob}
              onAssignAll={handleAssignAll}
              isLoading={isLoadingJobs}
              searchText={searchText}
              emptyMessage={
                <div className="flex flex-col gap-3">
                  <span>No Jobs found</span>
                  {searchText && (
                    <button
                      className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                      onClick={() => setSearchText('')}>
                      Reset search
                    </button>
                  )}
                </div>
              }
              getItemKey={(job) => job.p2p_id}
            />
          </ModalBody>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default AssignJobsModal;
